<template>
  <div>
    <!-- 证书图片预览弹窗 -->
    <a-modal
      title="机动人才证书"
      :visible="modalOpen"
      :footer="null"
      @cancel="handleClose"
      @update:visible="(val) => { 
        console.log('Modal @update:visible triggered:', val)
        modalOpen.value = val
        if (!val) emit('close') 
      }"
      :width="800"
    >
      <div v-if="imageList && imageList.length > 0" class="certificate-preview-container">
        <div class="carousel-wrapper">
          <a-button
            v-if="imageList.length > 1"
            class="carousel-nav-button prev-button"
            @click="handlePrev"
            :disabled="currentIndex === 0"
          >
            <LeftOutlined />
          </a-button>
          <a-carousel
            ref="carouselRef"
            :dots="true"
            :autoplay="false"
            effect="fade"
            :before-change="handleBeforeChange"
            style="height: 550px"
          >
            <div v-for="(imageUrl, index) in imageList" :key="index">
              <div
                style="
                  height: 550px;
                  text-align: center;
                  padding: 20px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                "
              >
                <img
                  :src="imageUrl"
                  alt="证书图片"
                  style="
                    max-width: 100%;
                    max-height: 500px;
                    cursor: pointer;
                    display: inline-block;
                  "
                  @click="handleImagePreview(imageUrl)"
                />
              </div>
            </div>
          </a-carousel>
          <a-button
            v-if="imageList.length > 1"
            class="carousel-nav-button next-button"
            @click="handleNext"
            :disabled="currentIndex === imageList.length - 1"
          >
            <RightOutlined />
          </a-button>
        </div>
        <div style="text-align: center; margin-top: 10px; color: #666">
          共 {{ imageList.length }} 张，当前第 {{ currentIndex + 1 }} 张，点击图片可放大查看
        </div>
      </div>
      <div v-else style="text-align: center; padding: 40px; color: #999">
        暂无证书图片
      </div>
    </a-modal>
    <!-- 单张图片预览弹窗 -->
    <a-modal
      :visible="imagePreviewVisible"
      :footer="null"
      :width="900"
      @cancel="imagePreviewVisible = false"
      @update:visible="(val) => imagePreviewVisible = val"
    >
      <img :src="previewImageUrl" alt="预览图片" style="width: 100%" />
    </a-modal>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue'
import { $emit } from '../../../../utils/gogocodeTransfer'

defineOptions({
  name: 'CertificatePreview',
})

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  certificateUrl: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['close'])

const imageList = ref([])
const imagePreviewVisible = ref(false)
const previewImageUrl = ref('')
const modalOpen = ref(false)
const currentIndex = ref(0)
const carouselRef = ref(null)

// 监听 modalOpen 的变化
watch(modalOpen, (newVal) => {
  console.log('modalOpen changed to:', newVal)
})

// 监听 visible 和 certificateUrl 的变化
watch(
  () => [props.visible, props.certificateUrl],
  ([newVisible, newUrl]) => {
    console.log('CertificatePreview watch triggered:', { newVisible, newUrl, currentModalOpen: modalOpen.value })
    modalOpen.value = newVisible
    console.log('After setting modalOpen.value:', modalOpen.value)
    if (newVisible && newUrl) {
      console.log('Calling init()')
      init()
    } else if (!newVisible) {
      // 关闭时清空数据
      imageList.value = []
      imagePreviewVisible.value = false
      previewImageUrl.value = ''
      currentIndex.value = 0
    }
  },
  { immediate: true }
)

/** 初始化 */
const init = () => {
  console.log('init() called with certificateUrl:', props.certificateUrl)
  if (!props.certificateUrl) {
    message.warning('该用户暂无证书图片', 3)
    return
  }
  // 将逗号分隔的URL字符串转换为数组
  imageList.value = props.certificateUrl
    .split(',')
    .map((url) => url.trim())
    .filter((url) => url)
  console.log('imageList.value:', imageList.value)
  if (imageList.value.length === 0) {
    message.warning('该用户暂无证书图片', 3)
    return
  }
}

/** 上一张 */
const handlePrev = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
    if (carouselRef.value) {
      carouselRef.value.prev()
    }
  }
}

/** 下一张 */
const handleNext = () => {
  if (currentIndex.value < imageList.value.length - 1) {
    currentIndex.value++
    if (carouselRef.value) {
      carouselRef.value.next()
    }
  }
}

/** 轮播图切换前回调 */
const handleBeforeChange = (from, to) => {
  currentIndex.value = to
}

/** 预览单张图片（点击放大） */
const handleImagePreview = (imageUrl) => {
  previewImageUrl.value = imageUrl
  imagePreviewVisible.value = true
}

/** 关闭弹窗 */
const handleClose = () => {
  modalOpen.value = false
  imageList.value = []
  imagePreviewVisible.value = false
  previewImageUrl.value = ''
  currentIndex.value = 0
  emit('close')
}
</script>

<style scoped>
.certificate-preview-container {
  position: relative;
}

.carousel-wrapper {
  position: relative;
}

.carousel-nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s;
}

.carousel-nav-button:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.7);
  color: #fff;
}

.carousel-nav-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.prev-button {
  left: 10px;
}

.next-button {
  right: 10px;
}
</style>
